'use client';

import Link from 'next/link';
import { useEffect, useRef } from 'react';
import { trackEvent } from '@/utils/analytics';
import { cn } from '@/utils/utils';
import { MOBILE_MENU_TOP_OFFSET } from './constants';
import { MobileDropdown } from './mobile-dropdown';
import type { MobileMenuProps } from './types';

export function MobileMenu({
  isOpen,
  onClose,
  navigationLinks,
  destinations,
  services,
}: MobileMenuProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) {
      return;
    }

    if (isOpen && !dialog.hasAttribute('open')) {
      dialog.showModal();
    } else if (!isOpen && dialog.hasAttribute('open')) {
      dialog.close();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const dialog = dialogRef.current;
      if (!dialog) {
        return;
      }

      const rect = dialog.getBoundingClientRect();
      const isInDialog =
        rect.top <= event.clientY &&
        event.clientY <= rect.top + rect.height &&
        rect.left <= event.clientX &&
        event.clientX <= rect.left + rect.width;

      if (!isInDialog) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const handleNavLinkClick = (label: string) => {
    trackEvent({
      action: 'mobile_nav_link_click',
      category: 'navigation',
      label,
      value: 1,
    });
    onClose();
  };

  const handleCtaClick = () => {
    trackEvent({
      action: 'mobile_get_in_touch_click',
      category: 'navigation',
      label: 'Mobile CTA',
      value: 1,
    });
    onClose();
  };

  return (
    <dialog
      ref={dialogRef}
      className={cn(
        'fixed left-0 right-0 bg-white rounded-4xl shadow-lg overflow-hidden transition-all duration-300 ease-in-out lg:hidden',
        'backdrop:bg-black/50 backdrop:backdrop-blur-sm',
        'p-0 m-0 w-[80%] mx-auto',
        isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
      )}
      style={{ top: `${MOBILE_MENU_TOP_OFFSET}px` }}
      aria-modal="true"
      aria-label="Mobile navigation menu"
    >
      <div className="p-4">
        {/* Destinations Dropdown */}
        <MobileDropdown
          title="Destinations"
          items={destinations}
          allItemsHref="/destinations"
          onClose={onClose}
          trackingCategory="destinations"
        />

        {/* Services Dropdown */}
        <MobileDropdown
          title="Services"
          items={services}
          allItemsHref="/services"
          onClose={onClose}
          trackingCategory="services"
        />

        <nav className="space-y-2 py-3">
          {navigationLinks.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className="block px-4 py-2 text-lg font-medium hover:text-accent"
              onClick={() => handleNavLinkClick(link.label)}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="mt-4">
          <Link
            href="/contact"
            className="block w-full text-center px-8 py-3.5 rounded-full bg-accent hover:bg-accent-foreground text-sm font-medium transition-colors duration-200"
            onClick={handleCtaClick}
          >
            Get In Touch
          </Link>
        </div>
      </div>
    </dialog>
  );
}
